import React, { useEffect, useState, useCallback } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { Sparkles, Loader2, CheckCircle } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';

export const AIQuotaCard = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [data, setData] = useState(null);
    const [loadingTier, setLoadingTier] = useState(null);
    const [polling, setPolling] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await api.get('/ai-quota/plans');
            setData(res.data);
        } catch { /* silent */ }
    }, []);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        const sessionId = searchParams.get('quota_session_id');
        if (!sessionId) return;
        let attempts = 0;
        setPolling(true);
        const poll = async () => {
            attempts += 1;
            try {
                const res = await api.get(`/ai-quota/checkout/status/${sessionId}`);
                if (res.data.payment_status === 'paid') {
                    toast.success('Extension du quota IA activée pour ce mois !');
                    setPolling(false);
                    setSearchParams({}, { replace: true });
                    load();
                    return;
                }
                if (res.data.status === 'expired') {
                    toast.error('Session de paiement expirée');
                    setPolling(false);
                    setSearchParams({}, { replace: true });
                    return;
                }
            } catch { /* retry */ }
            if (attempts < 8) setTimeout(poll, 2500);
            else { setPolling(false); toast.info('Vérification du paiement en cours, revenez dans un instant.'); }
        };
        poll();
    }, [searchParams, setSearchParams, load]);

    if (!data) return null;
    const pct = Math.min(100, Math.round((data.used / data.total) * 100));
    const barColor = pct >= 90 ? 'bg-red-500' : pct >= 70 ? 'bg-orange-500' : 'bg-gradient-to-r from-[#681793] to-[#FF914D]';

    const checkout = async (tierId) => {
        setLoadingTier(tierId);
        try {
            const res = await api.post('/ai-quota/checkout', { tier_id: tierId, origin_url: window.location.origin });
            window.location.href = res.data.url;
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de la création du paiement');
            setLoadingTier(null);
        }
    };

    return (
        <div className="rounded-2xl border border-border bg-card p-6" data-testid="ai-quota-card">
            <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#681793] to-[#FF914D] flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1">
                    <h3 className="font-bold text-foreground">Quota IA de l'agence</h3>
                    <p className="text-xs text-muted-foreground">
                        {data.included} questions/mois incluses, mutualisées entre vos {data.agents_count} utilisateurs
                    </p>
                </div>
            </div>

            <div className="mb-1 flex justify-between text-sm">
                <span className="text-muted-foreground">Consommation de {data.period}</span>
                <span className="font-semibold text-foreground" data-testid="ai-quota-usage">{data.used} / {data.total}</span>
            </div>
            <div className="h-2.5 rounded-full bg-muted overflow-hidden mb-4">
                <div className={`h-full rounded-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
            </div>

            {polling && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground py-2" data-testid="ai-quota-polling">
                    <Loader2 className="w-4 h-4 animate-spin" /> Vérification du paiement…
                </div>
            )}

            {!polling && data.extension && (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-green-500/10 border border-green-500/30" data-testid="ai-quota-extension-active">
                    <CheckCircle className="w-4 h-4 text-green-400 shrink-0" />
                    <p className="text-sm text-green-400 font-medium">
                        Extension {data.extension.tier_id === 'pro' ? 'Pro IA (+1000)' : 'Boost IA (+300)'} active ce mois-ci
                    </p>
                </div>
            )}

            {!polling && !data.extension && (
                <div className="grid sm:grid-cols-2 gap-3">
                    {data.tiers.map((t) => (
                        <div key={t.id} className="p-4 rounded-xl border border-border bg-background/50 flex flex-col" data-testid={`ai-quota-tier-${t.id}`}>
                            <p className="font-bold text-foreground text-sm">{t.label}</p>
                            <p className="text-xs text-muted-foreground mb-2">+{t.questions_added} questions pour le mois en cours</p>
                            <div className="mt-auto flex items-center justify-between gap-2">
                                <span className="text-lg font-black text-foreground">{t.amount}€ <span className="text-xs font-normal text-muted-foreground">HT</span></span>
                                <Button
                                    size="sm"
                                    onClick={() => checkout(t.id)}
                                    disabled={!!loadingTier}
                                    data-testid={`ai-quota-buy-${t.id}`}
                                    className="bg-gradient-to-r from-[#681793] to-[#FF914D] text-white hover:opacity-90"
                                >
                                    {loadingTier === t.id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Étendre'}
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
            <p className="text-[11px] text-muted-foreground mt-3">
                L'extension s'applique au mois en cours uniquement. Le quota inclus se renouvelle le 1er de chaque mois.
            </p>
        </div>
    );
};

export default AIQuotaCard;
